import { useEffect, useState } from 'react';
import { adminListLearners, deriveRevenue, adminStripeFinance } from '../../services/adminClient.js';
import { T, card, sectionTitle, eyebrow, PageHeader, StatCard, Scaffold } from './ui.jsx';

const money = (n, ccy = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: (ccy || 'usd').toUpperCase() }).format(n || 0);

export default function AdminFinance() {
  const [learners, setLearners] = useState(null);
  const [stripe, setStripe] = useState(null);   // null = loading; {configured,...}

  useEffect(() => {
    let c = false;
    adminListLearners().then(l => { if (!c) setLearners(l || []); }).catch(() => { if (!c) setLearners([]); });
    adminStripeFinance().then(s => { if (!c) setStripe(s || { configured: false }); });
    return () => { c = true; };
  }, []);

  if (!learners) return <p style={{ color: T.slate, fontSize: 13 }}>Loading finance…</p>;
  const rev = deriveRevenue(learners);
  const sprintTarget = 10;

  const live = stripe?.configured && !stripe?.error ? stripe : null;
  const paidCount = live ? live.subscriptions.active : rev.paid;
  const pct = Math.min(100, Math.round((paidCount / sprintTarget) * 100));
  const ccy = live?.currency || 'usd';

  return (
    <div>
      <PageHeader
        title="Finance"
        subtitle={live ? 'Live from Stripe — balance, MRR, and subscriptions.' : 'Derived from Supabase. Connect Stripe for live balance + MRR.'}
        right={<StripeStatus stripe={stripe} />}
      />

      {/* Top metrics — live MRR/paid when Stripe is connected, else the estimate. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        {live ? (
          <>
            <StatCard label="MRR (live)" value={money(live.mrr.active, ccy)} delta={`+${money(live.mrr.trialing, ccy)} in trials`} deltaColor={T.amber} tint={T.emerald} />
            <StatCard label="Active Subs" value={live.subscriptions.active} delta={`${live.subscriptions.trialing} trialing`} deltaColor={T.slate} tint={T.emerald} />
            <StatCard label="Balance" value={money(live.balance.available, ccy)} delta={`${money(live.balance.pending, ccy)} pending`} deltaColor={T.slate} tint={T.amber} />
            <StatCard label="Past Due" value={live.subscriptions.past_due} delta="needs attention" deltaColor={live.subscriptions.past_due ? T.coral : T.slate} tint={T.coral} />
          </>
        ) : (
          <>
            <StatCard label="Paid Users" value={rev.paid} delta="subscription active" deltaColor={T.emerald} tint={T.emerald} />
            <StatCard label="Est. MRR" value={`$${rev.blendedMrr.toFixed(2)}`} delta="blended estimate" deltaColor={T.slate} tint={T.amber} />
            <StatCard label="Infra Est." value={`$${rev.infra.toFixed(2)}`} delta="active × $2.00" deltaColor={T.slate} tint={T.slate} />
            <StatCard label="Est. Margin" value={`$${rev.margin.toFixed(2)}`} delta="MRR − infra" deltaColor={T.emerald} tint={T.emerald} />
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Sprint scoreboard */}
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 4 }}>Discovery Sprint — 10 paid users</p>
            <p style={{ fontSize: 12, color: T.slate, margin: '0 0 12px' }}>{live ? 'Active Stripe subscriptions' : 'Platform-side scoreboard'}</p>
            <div style={{ height: 12, background: '#eef0f4', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: T.amber, borderRadius: 99 }} />
            </div>
            <p style={{ fontSize: 13, fontWeight: 800, color: T.ink, margin: '10px 0 0' }}>{paidCount} / {sprintTarget} paid</p>
          </div>

          {/* Subscription mix — live from Stripe when connected */}
          {live ? (
            <div style={card}>
              <p style={{ ...sectionTitle, marginBottom: 12 }}>Subscription Mix</p>
              {[
                ['Active', live.subscriptions.active, T.emerald],
                ['Trialing', live.subscriptions.trialing, T.amber],
                ['Past due', live.subscriptions.past_due, T.coral],
                ['Canceled', live.subscriptions.canceled, T.slate],
              ].map(([label, n, c]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 12.5, color: T.slate }}>{label}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: c }}>{n}</span>
                </div>
              ))}
            </div>
          ) : (
            <Scaffold title="Per-tier MRR split" body="Connect Stripe (set STRIPE_SECRET_KEY) to compute MRR from real subscription prices. Until then MRR is a single blended estimate from Supabase." />
          )}
        </div>

        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <p style={{ ...sectionTitle, marginBottom: 12 }}>Recent Payouts</p>
            {live && live.payouts?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {live.payouts.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5 }}>
                    <span style={{ color: T.ink, fontWeight: 700 }}>{money(p.amount, p.currency)}</span>
                    <span style={{ color: p.status === 'paid' ? T.emerald : T.slate }}>
                      {p.status} · {p.arrival_date ? new Date(p.arrival_date * 1000).toLocaleDateString() : '—'}
                    </span>
                  </div>
                ))}
              </div>
            ) : live ? (
              <p style={{ fontSize: 12.5, color: T.slate, margin: 0 }}>No payouts yet.</p>
            ) : (
              <p style={{ fontSize: 13, color: T.slate, margin: 0, lineHeight: 1.55 }}>
                Live balance, payouts, and invoices appear once Stripe is connected. Learner rows already carry <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>stripe_customer_id</code> / <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>subscription_id</code> for linkage.
              </p>
            )}
          </div>
          <Scaffold title="Affiliate payouts (Stripe Connect)" body="Affiliate payout data appears once Stripe Connect is configured. Referral linkage (affiliate_id / referral_code) is already captured on signup." />
        </div>
      </div>
    </div>
  );
}

function StripeStatus({ stripe }) {
  if (stripe === null) return <span style={{ fontSize: 12, color: T.slate }}>Checking Stripe…</span>;
  if (stripe.configured && !stripe.error) {
    return <span style={{ fontSize: 12, fontWeight: 700, color: T.emerald, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.emerald }} /> Stripe connected
    </span>;
  }
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: T.coral, display: 'inline-flex', alignItems: 'center', gap: 6 }} title={stripe.error || ''}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.coral }} /> Stripe not connected
    </span>
  );
}
